import React, { useCallback, useState } from "react";
import { useEditorStore } from "../../../store";
// Hidden until multi-country forms ship — the "Plantilla de campos
// requeridos" picker confused merchants on single-country stores by
// suggesting the form supports multiple country templates at once.
// import { CountrySelector } from "../../common/CountrySelector";
import { useGetTemplates, TemplateDetail } from "../../../hooks/useTemplates";
import { useAuthenticatedFetch } from "../../../hooks/useAuthenticatedFetch";
import { ColorRow, SliderRow } from "./SettingRows";

export const DesignPanel: React.FC = () => {
  const designSettings = useEditorStore(
    (state) => state.config.design.settings
  );
  const currentTemplateId = useEditorStore(
    (state) => state.config.design.template_id
  );
  const updateDesignSetting = useEditorStore(
    (state) => state.updateDesignSetting
  );
  const setTemplateId = useEditorStore((state) => state.setTemplateId);
  const resetAllFieldBorders = useEditorStore(
    (state) => state.resetAllFieldBorders
  );
  const { templates } = useGetTemplates();
  const authFetch = useAuthenticatedFetch();
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);

  const handleSelectTemplate = useCallback(
    async (templateId: string) => {
      setIsApplyingTemplate(true);
      try {
        const res = await authFetch(`/api/admin/templates/${templateId}`);
        if (!res.ok) throw new Error(res.statusText);

        const detail: TemplateDetail = await res.json();
        const styles = detail.stylesConfig as Record<string, unknown>;

        // Apply each style from the template to the editor
        Object.entries(styles).forEach(([key, value]) => {
          updateDesignSetting(key, value);
        });

        setTemplateId(templateId);
      } catch (err) {
        console.error("Failed to apply template:", err);
        window.shopify?.toast?.show("Error al aplicar la plantilla", {
          isError: true,
        });
      } finally {
        setIsApplyingTemplate(false);
      }
    },
    [authFetch, updateDesignSetting, setTemplateId]
  );

  const handleReset = async () => {
    // 1. Apply the classic template — this is the source of truth for all visual styles
    const classicTemplate = templates?.find((t) => t.slug === "classic");
    if (classicTemplate) {
      await handleSelectTemplate(classicTemplate.id);
    }

    // 2. Reset non-template settings (not part of stylesConfig)
    updateDesignSetting("show_logo", false);
    updateDesignSetting("logo_url", "");
    updateDesignSetting("layout_mode", "embedded");
    updateDesignSetting("submit_button_text", "Completar Orden");

    // 3. Clear per-field border_color overrides in a single mutation
    resetAllFieldBorders();
  };

  return (
    <div className="p-5">
      <s-stack gap="large-200" direction="block">
        <s-text type="strong">Configuración del Diseño</s-text>

        <s-stack gap="base" direction="block">
          {/* Country Selector — hidden until multi-country forms ship.
              See the commented-out import at the top of this file. */}
          {/* <CountrySelector /> */}

          {/* Interaction Type (Visual Cards) */}
          {/* <div>
            <s-text type="strong" color="subdued">
              Tipo de interacción
            </s-text>
            <div className="grid grid-cols-1 gap-3 mt-2"> */}
          {/* Pop Up Card */}
          {/* <div
                className={`p-3 border rounded-lg cursor-pointer flex items-center gap-3 transition-all ${
                  designSettings?.layout_mode === "popup"
                    ? "border-emerald-500 ring-1 ring-emerald-500 bg-emerald-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => updateDesignSetting("layout_mode", "popup")}
              >
                <div
                  className={`p-2 rounded ${designSettings?.layout_mode === "popup" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-500"}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="9" y1="3" x2="9" y2="21" />
                  </svg>
                </div>
                <span
                  className={`font-medium ${designSettings?.layout_mode === "popup" ? "text-emerald-700" : "text-gray-700"}`}
                >
                  Pop Up
                </span>
              </div> */}

          {/* Embedded Card */}
          {/* <div
                className={`p-3 border rounded-lg cursor-pointer flex items-center gap-3 transition-all ${
                  !designSettings?.layout_mode ||
                  designSettings?.layout_mode === "embedded"
                    ? "border-emerald-500 ring-1 ring-emerald-500 bg-emerald-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => updateDesignSetting("layout_mode", "embedded")}
              >
                <div
                  className={`p-2 rounded ${!designSettings?.layout_mode || designSettings?.layout_mode === "embedded" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-500"}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                  </svg>
                </div>
                <span
                  className={`font-medium ${!designSettings?.layout_mode || designSettings?.layout_mode === "embedded" ? "text-emerald-700" : "text-gray-700"}`}
                >
                  Incorporado
                </span>
              </div>
            </div>
          </div> */}

          {/* <s-divider /> */}

          {/* Template Selection (Visual Cards) */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <s-text type="strong" color="subdued">
                Temas, Estilos y Colores
              </s-text>
              <button
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                onClick={handleReset}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74-2.74L3 12" />
                </svg>
                Restablecer ajustes
              </button>
            </div>
            <p className="text-xs text-gray-500 pb-2 pt-2">
              Selecciona una plantilla
            </p>

            <div className="grid grid-cols-3 gap-2">
              {templates.map((template) => {
                const isSelected = currentTemplateId === template.id;
                return (
                  <div
                    key={template.id}
                    className={`p-2 border rounded-lg flex flex-col items-center gap-2 text-center transition-all ${
                      isApplyingTemplate
                        ? "opacity-50 pointer-events-none"
                        : "cursor-pointer"
                    } ${
                      isSelected
                        ? "border-emerald-500 ring-1 ring-emerald-500 bg-emerald-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() =>
                      !isApplyingTemplate && handleSelectTemplate(template.id)
                    }
                  >
                    {template.previewImageUrl ? (
                      <img
                        src={template.previewImageUrl}
                        alt={template.name}
                        className="w-full h-16 object-cover rounded"
                      />
                    ) : (
                      <div
                        className={`p-1.5 rounded-full ${isSelected ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-500"}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <line x1="3" y1="9" x2="21" y2="9" />
                        </svg>
                      </div>
                    )}
                    <span
                      className={`text-xs font-medium ${isSelected ? "text-emerald-700" : "text-gray-700"}`}
                    >
                      {template.name}
                      {template.isPremium && " (Pro)"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <s-divider />

          {/* Form Title (Preview Header) */}
          <div>
            <s-text type="strong" color="subdued">
              Configuración de la vista previa
            </s-text>
            <div className="mt-2 space-y-3">
              <s-text-field
                label="Título del Formulario (Preview)"
                name="form_title"
                value={designSettings?.form_title || ""}
                placeholder="Preview"
                onInput={(event: unknown) => {
                  const value = (event as { currentTarget: { value: string } })
                    .currentTarget.value;
                  updateDesignSetting("form_title", value);
                }}
              />
              <s-text-field
                label="Texto del botón de envío"
                name="submit_button_text"
                value={designSettings?.submit_button_text || ""}
                placeholder="Comprar - $100,000"
                onInput={(event: unknown) => {
                  const value = (event as { currentTarget: { value: string } })
                    .currentTarget.value;
                  updateDesignSetting("submit_button_text", value);
                }}
              />
            </div>
          </div>

          <s-divider />

          {/* Custom Logo */}
          <div>
            <s-text type="strong" color="subdued">
              Logo personalizado
            </s-text>
            <div className="mt-2">
              {designSettings?.logo_url ? (
                <div className="w-full h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative overflow-hidden">
                  <img
                    src={designSettings?.logo_url}
                    alt="Logo"
                    className="max-h-full max-w-full object-contain p-2"
                  />
                  <button
                    className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-50 text-red-500 z-20"
                    onClick={() => {
                      updateDesignSetting("logo_url", "");
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="w-full h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400 mb-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-xs text-gray-500">
                    Pega la URL de tu logo
                  </span>
                </div>
              )}
              <div className="mt-2">
                <s-text-field
                  label="URL del logo"
                  name="logo_url"
                  placeholder="https://ejemplo.com/logo.png"
                  value={designSettings?.logo_url || ""}
                  onInput={(event: unknown) => {
                    const newValue = (
                      event as { currentTarget: { value: string } }
                    ).currentTarget.value;
                    updateDesignSetting("logo_url", newValue);
                    if (newValue && !designSettings?.show_logo) {
                      updateDesignSetting("show_logo", true);
                    }
                  }}
                />
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                Recomendado: 600px o mayor
              </div>
            </div>
          </div>

          {/* --- TYPOGRAPHY SECTION --- */}
          <s-divider />
          <div>
            <s-text type="strong" color="subdued">
              Tipografía
            </s-text>
            <div className="mt-4 flex flex-col gap-5">
              {/* Font Family (Existing) */}
              <s-select
                label="Fuente"
                name="font_family"
                value={designSettings?.font_family || "Inter, sans-serif"}
                onChange={(event: Event) =>
                  updateDesignSetting(
                    "font_family",
                    (event.target as HTMLSelectElement).value
                  )
                }
              >
                <s-option value="Inter, sans-serif">Inter</s-option>
                <s-option value="'Roboto', sans-serif">Roboto</s-option>
                <s-option value="'Open Sans', sans-serif">Open Sans</s-option>
                <s-option value="'Lato', sans-serif">Lato</s-option>
                <s-option value="'Montserrat', sans-serif">Montserrat</s-option>
                <s-option value="'Oswald', sans-serif">Oswald</s-option>
                <s-option value="'Playfair Display', serif">
                  Playfair Display
                </s-option>
              </s-select>

              {/* Font Size */}
              <SliderRow
                label="Tamaño del texto"
                value={designSettings?.font_size || 16}
                min={12}
                max={24}
                onChange={(value) => updateDesignSetting("font_size", value)}
              />

              {/* Text Color */}
              <ColorRow
                label="Color del texto"
                name="text_color"
                value={designSettings?.text_color}
                fallback="#000000"
                onChange={(value) => updateDesignSetting("text_color", value)}
              />
            </div>
          </div>

          {/* --- CONTAINER STYLE SECTION --- */}
          <s-divider />
          <div>
            <s-text type="strong" color="subdued">
              Estilo del Contenedor
            </s-text>
            <div className="mt-4 flex flex-col gap-5">
              {/* Background Color */}
              <ColorRow
                label="Color del fondo"
                name="background_color"
                value={designSettings?.background_color}
                fallback="#ffffff"
                onChange={(value) =>
                  updateDesignSetting("background_color", value)
                }
              />

              {/* Corner Radius */}
              <SliderRow
                label="Radio del borde"
                value={designSettings?.corner_radius ?? 4}
                min={0}
                max={20}
                onChange={(value) =>
                  updateDesignSetting("corner_radius", value)
                }
              />

              {/* Border Color */}
              <ColorRow
                label="Color del borde"
                name="border_color"
                value={designSettings?.border_color}
                fallback="#e5e7eb"
                onChange={(value) => updateDesignSetting("border_color", value)}
              />

              {/* Border Width */}
              <SliderRow
                label="Ancho del borde"
                value={designSettings?.border_width || 0}
                min={0}
                max={10}
                onChange={(value) => updateDesignSetting("border_width", value)}
              />

              {/* Shadow Intensity */}
              <SliderRow
                label="Sombra"
                value={designSettings?.shadow_intensity || 0}
                min={0}
                max={5}
                unit=""
                onChange={(value) =>
                  updateDesignSetting("shadow_intensity", value)
                }
              />
            </div>
          </div>

          {/* --- FIELDS STYLE SECTION --- */}
          <s-divider />
          <div>
            <s-text type="strong" color="subdued">
              Estilo de Campos
            </s-text>
            <div className="mt-4 flex flex-col gap-5">
              {/* Field Background Color */}
              <ColorRow
                label="Color de fondo del campo"
                name="field_background_color"
                value={designSettings?.field_background_color}
                fallback="#ffffff"
                onChange={(value) =>
                  updateDesignSetting("field_background_color", value)
                }
              />

              {/* Field Text Color */}
              <ColorRow
                label="Color de texto del campo"
                name="field_text_color"
                value={designSettings?.field_text_color}
                fallback="#000000"
                onChange={(value) =>
                  updateDesignSetting("field_text_color", value)
                }
              />

              {/* Field Prefix Background Color */}
              <ColorRow
                label="Color de fondo del prefijo"
                name="field_prefix_background_color"
                value={designSettings?.field_prefix_background_color}
                fallback="#f3f4f6"
                onChange={(value) =>
                  updateDesignSetting("field_prefix_background_color", value)
                }
              />
            </div>
          </div>

          {/* Primary Color */}
          <ColorRow
            label="Color primario"
            name="primary_color"
            value={designSettings?.primary_color as string}
            fallback="#008060"
            onChange={(value) => updateDesignSetting("primary_color", value)}
          />

          {/* Show Logo Toggle */}
          <div>
            <s-select
              label="Mostrar Logo"
              name="show_logo"
              value={designSettings?.show_logo ? "true" : "false"}
              onChange={(event) => {
                const value = event.currentTarget.value;
                updateDesignSetting("show_logo", value === "true");
              }}
            >
              <s-option value="false">Oculto</s-option>
              <s-option value="true">Visible</s-option>
            </s-select>
          </div>
        </s-stack>
      </s-stack>
    </div>
  );
};
